import {
    PowderguideApiInterface,
    EventRequest,
    EventCategory,
    EventMonth
} from './powderguide-api'
import { PaginationController } from './pagination-controller'
import { UrlHashHandler } from './url-hash-handler'

// A function rather than a frozen object: freezing is shallow, and a shared
// `categories` array would be mutated by toggleCategory().
const defaultRequest = (): EventRequest => ({
    page: 1,
    page_size: 24,
    categories: [ ],
});

export class PowderguideEvents {
    public loading: boolean = true;
    public error: string|null = null;
    public months: EventMonth[];
    public categories: EventCategory[];
    public readonly pagination: PaginationController = new PaginationController({
        page: 1,
        next_page: 2,
        previous_page: null,
        last_page: 1,
        per_page: 24,
        from_item: 0,
        to_item: 24,
        total_items: 0,
    }, { startFixedElements: 2, endFixedElements: 2, elementsAroundCurrent: 2 }, () => this.loadPage(this.pagination.page));
    public request: EventRequest = defaultRequest();
    private hashHandler: UrlHashHandler<[ 'powderguide_events[page]', 'powderguide_events[categories]' ]> = new UrlHashHandler([ 'powderguide_events[page]', 'powderguide_events[categories]' ]);

    constructor(
        private api: PowderguideApiInterface,
        Alpine: any
    ) {
        this.months = Alpine.reactive([ ]);
        this.categories = Alpine.reactive([ ]);
    }


    public async init(config: Partial<{ paginate: number }> = { }): Promise<void> {
        if (config.paginate) {
            this.pagination.information.per_page = config.paginate;
            this.request.page_size = config.paginate;
        }
        this.readFromHash();
        await this.updateEvents();
    }

    public async loadPage(page: number): Promise<void> {
        this.request.page = page;
        await this.updateEvents();
        this.writeToHash();
    }

    private async updateEvents(): Promise<void> {
        try {
            this.loading = true;
            this.error = null;
            const result = await this.api.fetchEvents(this.request);
            this.months.length = 0;
            this.months.push(...result.data);
            this.categories.length = 0;
            this.categories.push(...result.categories);
            this.pagination.update(result.pagination);
            this.request.page = result.pagination.page;
            this.request.page_size = result.pagination.per_page;
            this.writeToHash();
        } catch (e) {
            this.error = e.message;
        } finally {
            this.loading = false;
        }
    }

    public get filtersCustomized(): boolean {
        return this.request.categories.length > 0;
    }

    public async clearFilter(): Promise<void> {
        // Keep the page size the list was initialised with.
        this.request = { ...defaultRequest(), page_size: this.request.page_size };
        await this.updateEvents();
        this.writeToHash();
    }

    public hasCategory(category: string): boolean {
        return this.request.categories.indexOf(category) !== -1;
    }

    public async toggleCategory(category: string): Promise<void> {
        if (this.hasCategory(category)) {
            this.request.categories = this.request.categories.filter(c => c !== category);
        } else {
            this.request.categories.push(category);
        }

        this.request.page = 1;
        await this.updateEvents();
        this.writeToHash();
    }

    private readFromHash(): void {
        const properties = this.hashHandler.read();
        if (properties['powderguide_events[page]']) {
            this.request.page = parseInt(properties['powderguide_events[page]']);
        }
        if (properties['powderguide_events[categories]']) {
            this.request.categories = properties['powderguide_events[categories]'].split(',').map(c => c.trim()).filter(c => c.length > 0);
        }
    }

    private writeToHash(): void {
        this.hashHandler.write({
            'powderguide_events[page]': this.request.page > 1 ? this.request.page.toString() : null,
            'powderguide_events[categories]': this.request.categories.join(','),
        });
    }
}
